import { createTheme, typographyClasses } from "@mui/material"
import * as muiColors from "@mui/material/colors"
import { blue, green, grey, orange, red } from "@mui/material/colors"
import {
	PALETTE_COLOR_DECLINATIONS_TONES,
	type PaletteColorDeclinationsTones,
	type PaletteColorDeclinationsTonesKeys,
	type PaletteColorDeclinationToneValue,
} from "./types"

const allColorsKeys = Object.keys(muiColors) as (keyof typeof muiColors)[]
type ColorsKeys = (typeof allColorsKeys)[number]
type ColorsDeclinationsTones = (typeof muiColors)[ColorsKeys]

const fontSize = 14

export const commonTheme = createTheme({
	cssVariables: {
		colorSchemeSelector: "data",
	},
	palette: {
		mode: "light", // todo: fix override of this theme in light mode for recent versions of mui
		tonalOffset: 0.2,
		primary: {
			main: blue[500],
			contrastText: "#fff",
		},
		secondary: {
			main: grey[600],
		},
		error: {
			main: red[500],
		},
		warning: {
			main: orange[800],
		},
		success: {
			main: "#34D399",
			contrastText: "#fff",
		},
		green,
		grey,
		...colorsDeclinations(["red", "blue", "yellow"]),
	},
	components: {
		MuiSvgIcon: {
			variants: [
				{
					props: { fontSize: "xs" },
					style: {
						fontSize,
					},
				},
			],
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					fontSize,
				},
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					fontSize,
				},
			},
		},
		MuiInputAdornment: {
			styleOverrides: {
				root: {
					[`& .${typographyClasses.root}`]: {
						fontSize,
					},
				},
			},
		},
		MuiChip: {
			defaultProps: {
				size: "small",
			},
		},
		MuiMenu: {
			defaultProps: {
				elevation: 3,
			},
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					variants: [
						{
							props: { fullWidth: true },
							style: {
								width: "100%",
							},
						},
						{
							props: { maxWidth: "lg" },
							style: {
								maxWidth: "300px",
							},
						},
						{
							props: { maxWidth: "xl" },
							style: {
								maxWidth: "500px",
							},
						},
					],
				},
			},
		},
		MuiButton: {
			defaultProps: {
				disableElevation: true,
				disableRipple: true,
				variant: "contained",
				color: "primary",
			},
			styleOverrides: {
				startIcon: {
					marginRight: "4px",
				},
				root: {
					padding: "4px 8px",
					textTransform: "none",
					fontWeight: 400,
					fontSize,
				},
			},
		},
	},
	typography: {
		fontSize,
		fontFamily: [
			"ui-sans-serif",
			"system-ui",
			"sans-serif",
			"Apple Color Emoji",
			"Segoe UI Emoji",
			"Segoe UI Symbol",
			"Noto Color Emoji",
		].join(","),
	},
	shape: {
		borderRadius: 4,
	},
})

export function colorsDeclinations<C extends Partial<ColorsKeys>>(colors: C[]) {
	return colors.reduce(
		(colorsAcc, color) => {
			// biome-ignore lint/performance/noDynamicNamespaceImportAccess: should be dynamic there
			const mappedColor = muiColors[color]
			colorsAcc[color] = PALETTE_COLOR_DECLINATIONS_TONES.reduce(
				(tonesAcc, tone) => {
					tonesAcc[tone] = mappedColor[tone as keyof ColorsDeclinationsTones]
					return tonesAcc
				},
				{} as Record<PaletteColorDeclinationsTonesKeys, PaletteColorDeclinationToneValue>,
			)
			return colorsAcc
		},
		{} as Record<C, PaletteColorDeclinationsTones>,
	)
}
